/**
 * AdSense placement experiment — pure decision logic.
 *
 * Why this exists (2026-09-21): AdSense was made eligible on every slot by
 * owner directive, which trades away first-party conversion (Pro upsell,
 * affiliate) for ad revenue on the AdSense-eligible slots. The size of that
 * trade is unknown, so it is a measured experiment, tunable from PostHog with
 * no deploy.
 *
 * Three knobs, all in PostHog:
 *
 *  1. FLAG `adsense-slot-experiment` (multivariate: `adsense` | `first_party`).
 *     The variant split IS the A/B: `first_party` visitors get the pre-#5539
 *     experience (no AdSense on the slot), `adsense` visitors get AdSense. A flag
 *     that is absent, off, unresolved or at 0% rollout returns no variant and the
 *     visitor gets the DEFAULT arm below, so shipping this changes nothing until
 *     someone configures PostHog.
 *  2. PAYLOAD `monetization-settings` -> `adsense.disabledSlots`: turn AdSense
 *     off for individual slots (e.g. `["feed-sidebar"]`).
 *  3. PAYLOAD `monetization-settings` -> `adsense.disabledMarkets`: turn AdSense
 *     off for a market (e.g. `["indonesia"]` where the Shopee affiliate is the
 *     better earner and AdSense should only serve the global audience).
 *
 * Precedence, first match wins: disabled slot, disabled market, experiment
 * `first_party`, experiment `adsense`, default. Overrides (1 and 2 in the
 * payload) are labelled `override` rather than an experiment arm so a hand-tuned
 * slot never pollutes the arm comparison.
 *
 * This file is deliberately free of React and PostHog so the whole decision
 * table is unit-testable.
 */

export const ADSENSE_SLOT_EXPERIMENT_FLAG = "adsense-slot-experiment";

/** The two PostHog variants of the experiment flag. */
export const ADSENSE_ARMS = ["adsense", "first_party"] as const;
export type AdsenseArm = (typeof ADSENSE_ARMS)[number];

/**
 * What a visitor was actually served:
 * - `adsense` / `first_party`: bucketed by the experiment flag;
 * - `default`: no variant resolved (flag absent, off, 0% rollout, or PostHog
 *   never answered) — AdSense allowed, same as before the experiment existed;
 * - `override`: AdSense withheld by a payload override, not by the experiment.
 */
export type AdsenseArmLabel = AdsenseArm | "default" | "override";

export type AdsenseMarket = "indonesia" | "global";

export interface AdsenseConfig {
	/** Slot names (see `SlotName` in ./zones) where AdSense is withheld. */
	disabledSlots: string[];
	/** Markets where AdSense is withheld. */
	disabledMarkets: AdsenseMarket[];
}

/** The always-safe floor: nothing disabled, current behaviour. */
export const DEFAULT_ADSENSE_CONFIG: AdsenseConfig = {
	disabledSlots: [],
	disabledMarkets: [],
};

export type AdsenseArmReason =
	| "slot_disabled"
	| "market_disabled"
	| "experiment_first_party"
	| "experiment_adsense"
	| "default";

export interface AdsenseArmDecision {
	/** Whether a real AdSense unit may render in this slot for this visitor. */
	allowed: boolean;
	label: AdsenseArmLabel;
	reason: AdsenseArmReason;
}

export function resolveAdsenseArm(input: {
	slot: string;
	market: string;
	/**
	 * Experiment variant key as normalised by `useExperiment`: `adsense`,
	 * `first_party`, or `control` (flag off / unresolved). Anything else is
	 * treated as `control`.
	 */
	variant: string;
	config: AdsenseConfig;
}): AdsenseArmDecision {
	const { slot, market, variant, config } = input;

	if (config.disabledSlots.includes(slot)) {
		return { allowed: false, label: "override", reason: "slot_disabled" };
	}
	if ((config.disabledMarkets as string[]).includes(market)) {
		return { allowed: false, label: "override", reason: "market_disabled" };
	}
	if (variant === "first_party") {
		return {
			allowed: false,
			label: "first_party",
			reason: "experiment_first_party",
		};
	}
	if (variant === "adsense") {
		return { allowed: true, label: "adsense", reason: "experiment_adsense" };
	}
	return { allowed: true, label: "default", reason: "default" };
}

/**
 * D1 carries the arm in `ad_affiliate_events.variant_id` (an existing column, no
 * migration) so the arms can be compared from D1 alone — PostHog is where this
 * project has been burned (flag-quota blowout, replay tables returning nothing).
 *
 * Shape: `asx:<label>:<market>`, e.g. `asx:first_party:indonesia`. The `asx:`
 * prefix keeps it separable from the offer copy-A/B ids that share the column,
 * and the value passes the telemetry route's `[A-Za-z0-9_.:-]{1,64}` check.
 */
export const ADSENSE_VARIANT_PREFIX = "asx:";

export function adsenseVariantId(
	label: AdsenseArmLabel,
	market: string,
): string {
	const safeMarket = /^[a-z_]{1,20}$/.test(market) ? market : "unknown";
	return `${ADSENSE_VARIANT_PREFIX}${label}:${safeMarket}`;
}

export interface ParsedAdsenseVariantId {
	label: AdsenseArmLabel;
	market: string;
}

/** Inverse of `adsenseVariantId`; `null` for any id that is not ours. */
export function parseAdsenseVariantId(
	id: string | null | undefined,
): ParsedAdsenseVariantId | null {
	if (!id?.startsWith(ADSENSE_VARIANT_PREFIX)) return null;
	const [label, market] = id.slice(ADSENSE_VARIANT_PREFIX.length).split(":");
	if (
		label !== "adsense" &&
		label !== "first_party" &&
		label !== "default" &&
		label !== "override"
	) {
		return null;
	}
	return { label, market: market || "unknown" };
}
